/**
 * SelecaoPrecosPage — escolha dos preços pesquisados (F6a).
 *
 * É o estado AGUARDANDO_SELECAO do contrato de tasks: a pesquisa terminou
 * e a geração do ETP só continua depois que o usuário escolhe a cesta.
 * Art. 23 da Lei 14.133/2021 — a estimativa se embasa em um conjunto de
 * preços, não em um. Por isso o mínimo vem do backend e é obrigatório.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faCheckDouble,
  faExclamationTriangle,
  faExternalLinkAlt,
  faMagnifyingGlassDollar,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { confirmarPrecos, pesquisaPrecos } from '../../api/etp'
import { useToast } from '../../components/Toast'

export function SelecaoPrecosPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const { notificar } = useToast()
  const [escolhidos, setEscolhidos] = useState<Set<number>>(new Set())

  const { data, isLoading, error } = useQuery({
    queryKey: ['etp-pesquisa-precos', idNumerico],
    queryFn: () => pesquisaPrecos(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const confirmar = useMutation({
    mutationFn: () => confirmarPrecos(idNumerico, [...escolhidos]),
    onSuccess: (resultado) => {
      notificar('success', `${resultado.selecionados} preços confirmados. Retomando a geração…`)
      navegar(`/tasks/etp/${idNumerico}`)
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando resultados da pesquisa…
      </p>
    )
  }
  if (error || !data) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faMagnifyingGlassDollar} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Nenhuma pesquisa pendente
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'Não há preços aguardando seleção neste processo.'}
        </p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const faltam = Math.max(0, data.minimo_selecao - escolhidos.size)
  const alternar = (indice: number) =>
    setEscolhidos((atual) => {
      const proximo = new Set(atual)
      if (proximo.has(indice)) proximo.delete(indice)
      else proximo.add(indice)
      return proximo
    })

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 p-6 text-white">
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon icon={faMagnifyingGlassDollar} className="mr-3" />
            Seleção de Preços
          </h1>
          <p className="mt-1 text-amber-100">
            A pesquisa terminou. Escolha as contratações que vão embasar a
            estimativa de valor (Art. 23 da Lei 14.133/2021).
          </p>
        </div>
        <div className="p-6">
          <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
            <strong>Objeto:</strong> {data.objeto}
          </p>
          {data.termo_pesquisa && (
            <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
              <strong>Termo pesquisado:</strong> {data.termo_pesquisa}
            </p>
          )}
          {Object.keys(data.estatisticas).length > 0 && (
            <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {Object.entries(data.estatisticas).map(([chave, valor]) => (
                <div key={chave} className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <dt className="text-xs uppercase text-slate-500">{chave.replace(/_/g, ' ')}</dt>
                  <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100">{valor}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </header>

      <div
        className={`sticky top-0 z-10 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 shadow-sm ${
          faltam > 0
            ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30'
            : 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/30'
        }`}
      >
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          <FontAwesomeIcon
            icon={faltam > 0 ? faExclamationTriangle : faCheckCircle}
            className={`mr-2 ${faltam > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
          />
          {escolhidos.size} de {data.resultados.length} selecionados
          {faltam > 0
            ? ` — faltam ${faltam} para o mínimo de ${data.minimo_selecao}`
            : ' — mínimo atingido'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              setEscolhidos(
                escolhidos.size === data.resultados.length
                  ? new Set()
                  : new Set(data.resultados.map((r) => r.indice)),
              )
            }
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-white dark:border-slate-600 dark:text-slate-200"
          >
            <FontAwesomeIcon icon={faCheckDouble} className="mr-2" />
            {escolhidos.size === data.resultados.length ? 'Limpar seleção' : 'Selecionar todos'}
          </button>
          <button
            type="button"
            disabled={faltam > 0 || confirmar.isPending}
            onClick={() => confirmar.mutate()}
            className="rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 px-6 py-2 text-sm font-semibold text-white shadow transition-all hover:from-emerald-600 hover:to-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={confirmar.isPending ? faSpinner : faCheckCircle}
              spin={confirmar.isPending}
              className="mr-2"
            />
            Confirmar e continuar
          </button>
        </div>
      </div>

      <ul className="space-y-3">
        {data.resultados.map((r) => {
          const marcado = escolhidos.has(r.indice)
          return (
            <li key={r.indice}>
              <label
                className={`flex cursor-pointer gap-4 rounded-xl border-2 p-4 transition-colors ${
                  marcado
                    ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-900/20'
                    : 'border-slate-200 bg-white hover:border-indigo-300 dark:border-slate-700 dark:bg-dark-card'
                }`}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => alternar(r.indice)}
                  aria-label={`Selecionar contratação ${r.indice + 1}`}
                  className="mt-1 h-5 w-5 accent-emerald-600"
                />
                <div className="min-w-0 flex-1">
                  <p className="mb-1 font-semibold text-slate-800 dark:text-slate-100">
                    {r.objeto || 'Contratação sem descrição'}
                  </p>
                  {r.orgao && (
                    <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">{r.orgao}</p>
                  )}
                  <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
                    {r.valor_unitario && (
                      <div>
                        <dt className="text-xs text-slate-500">Valor unitário</dt>
                        <dd className="font-semibold text-emerald-700 dark:text-emerald-400">{r.valor_unitario}</dd>
                      </div>
                    )}
                    {r.valor_total && (
                      <div>
                        <dt className="text-xs text-slate-500">Valor total</dt>
                        <dd className="font-medium text-slate-700 dark:text-slate-300">{r.valor_total}</dd>
                      </div>
                    )}
                    {r.quantidade && (
                      <div>
                        <dt className="text-xs text-slate-500">Quantidade</dt>
                        <dd className="text-slate-700 dark:text-slate-300">
                          {r.quantidade} {r.unidade}
                        </dd>
                      </div>
                    )}
                    {r.data && (
                      <div>
                        <dt className="text-xs text-slate-500">Data</dt>
                        <dd className="text-slate-700 dark:text-slate-300">{r.data}</dd>
                      </div>
                    )}
                  </dl>
                  {r.link && (
                    <a
                      href={r.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-2 inline-flex items-center text-xs font-medium text-blue-600 hover:underline"
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-1" />
                      Ver contratação na fonte {r.fonte && `(${r.fonte})`}
                    </a>
                  )}
                </div>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
